import useSWR, { SWRResponse } from 'swr'
import { AxiosError } from 'axios'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Container,
  Typography,
  Box,
  Grid,
  Button,
  Card,
  CardContent,
  Stack,
  Link,
} from '@mui/material'
import { IconUser } from '@tabler/icons-react'
import { FileUpload, MailOutline, PhoneOutlined, TaskOutlined } from '@mui/icons-material'
import Loading from '../../components/Loading'
import axiosInstance from '../../utils/axiosInstance'
import recruspinWhiteLogo from '../../assets/images/ recruspin-white.png'
import { fixImgSrc, truncateHtmlSafely } from '../../utils/tools'
import { validateFileSize } from '../../utils/fileValidation'
import './style.scss'

interface ApplicationData {
  data: { order: any; lead: any }
  order: {
    id: string
    jobName: string
    place: string
    adDescription: string
    createdAt: string
    headerImage?: {
      id: string
      name: string
      image: {
        url: string
      }
    } | null
    customer: {
      id: string
      name: string
      logoUrl?: string
    }
  }
  lead: {
    id: string
    fieldData: any[]
    createdAt: string
    platform: string
  }
}

const ApplicationPage = () => {
  const [searchParams] = useSearchParams()
  const orderId = searchParams.get('o')
  const leadId = searchParams.get('l')

  const [cvFile, setCvFile] = useState<File | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [isDragging, setIsDragging] = useState<boolean>(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [expanded, setExpanded] = useState(false)

  const {
    data: response,
    error,
    isLoading,
  }: SWRResponse = useSWR(
    orderId && leadId ? `/application?o=${orderId}&l=${leadId}` : null,
    axiosInstance
  )

  const applicationData: ApplicationData | null = response?.data || null

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || file.type !== 'application/pdf') {
      setSubmitError('Please select a PDF file.')
      return
    }
    const sizeError = validateFileSize([file])
    if (sizeError) {
      setSubmitError(sizeError)
      return
    }
    setCvFile(file)
    setSubmitError('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!cvFile) {
      setSubmitError('Please upload your CV.')
      return
    }

    setIsSubmitting(true)
    setSubmitError('')

    try {
      const formData = new FormData()
      formData.append('cv', cvFile)
      formData.append('orderId', orderId!)
      formData.append('leadId', leadId!)

      const response = await axiosInstance.post('/application/submit-cv', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      if (response.status === 200) {
        setSubmitSuccess(true)
        setCvFile(null)
      } else {
        throw new Error('Server returned an error response')
      }
    } catch (error) {
      console.error('Error submitting CV:', error)
      const serverMessage = (error as AxiosError<{ message?: string }>)?.response?.data?.message
      setSubmitError(serverMessage || 'There was a problem uploading. Please try again later.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const formatFieldData = (fieldData: any[]) => {
    if (!fieldData || !Array.isArray(fieldData)) return []

    return fieldData.map((field) => ({
      name: field?.name || 'Unknown',
      values: Array.isArray(field?.values) ? field.values : [field?.values || ''],
    }))
  }

  if (error || !applicationData || !orderId || !leadId) {
    return (
      <Container maxWidth="lg" className="application-error">
        <Typography variant="h4" component="h1" gutterBottom>
          {error?.message || 'Bewerbung nicht gefunden'}
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Die angeforderte Bewerbungsseite konnte nicht geladen werden.
        </Typography>
      </Container>
    )
  }

  const { order, lead } = applicationData.data

  const { html: truncatedHtml, isTruncated } = truncateHtmlSafely(order?.adDescription, 250)

  const formattedFields = formatFieldData(lead?.fieldData || [])
  if (isLoading) {
    return <Loading />
  }

  return (
    <div className="application-page">
      <Stack
        pl={12}
        width="100%"
        height={99}
        justifyContent="center"
        sx={{
          background: `
          linear-gradient(
            90deg,
            #220D77 0%,
            #1A0A3F 40%,
            #14082F 70%,
            #0E061F 100%
          )
        `,
        }}
      >
        <img src={recruspinWhiteLogo} width={243} height={52} alt="logo" />
      </Stack>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={8}>
          {/* Left side - Job Information */}
          <Grid item xs={12} lg={7}>
            <Stack spacing={5}>
              <Stack direction="row" spacing={5}>
                <Stack>
                  {order?.customer?.logoUrl && (
                    <img
                      src={fixImgSrc(order.customer.logoUrl)}
                      alt={`${order.customer.name} logo`}
                      style={{
                        maxWidth: 135,
                        maxHeight: 97,
                        objectFit: 'contain',
                      }}
                    />
                  )}
                </Stack>
                <Stack alignSelf="center" height="100%" spacing={1}>
                  <Typography variant="h2" color="primary">
                    {order.jobName}
                  </Typography>

                  <Stack direction="row" spacing={3}>
                    <Stack direction="row" spacing={1}>
                      <IconUser width={20} height={20} color="#220d77" />
                      <Typography variant="body2" color="primary">
                        {order.customer.name}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      <LocationOnIcon color="primary" fontSize="small" />
                      <Typography variant="body2" color="primary">
                        {order.place}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      <CalendarTodayIcon color="primary" fontSize="small" />
                      <Typography variant="body2" color="primary">
                        {new Date(order.createdAt).toLocaleDateString('de-DE')}
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
              <Box
                className="job-description-content"
                sx={{
                  color: '#220d77',
                  '& *': {
                    color: '#220d77',
                  },
                }}
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: expanded ? order?.adDescription || '' : truncatedHtml,
                  }}
                />
                {isTruncated && (
                  <Typography
                    fontWeight={700}
                    variant="button"
                    onClick={() => setExpanded(!expanded)}
                    color="primary"
                    sx={{
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      userSelect: 'none',
                    }}
                  >
                    {expanded ? 'Show less' : 'Show more'}
                    {expanded ? (
                      <KeyboardArrowUpIcon fontSize="medium" />
                    ) : (
                      <KeyboardArrowDownIcon fontSize="medium" />
                    )}
                  </Typography>
                )}
              </Box>
              {submitSuccess ? (
                <Stack
                  width="100%"
                  height={305}
                  mt={5}
                  borderRadius={7}
                  alignItems="center"
                  justifyContent="center"
                  spacing={2}
                  sx={{
                    background: `
    radial-gradient(
      circle at top left,
      #E9F8CC 0%,
      #E9F8CC 30%,
      transparent 60%
    ),
    linear-gradient(white, white)
  `,
                    boxShadow: '0px 0px 50px rgba(0,0,0,0.25)',
                  }}
                >
                  <TaskOutlined
                    sx={{
                      fontSize: 40,
                    }}
                    htmlColor="primary"
                  />
                  <Typography variant="h3" color="primary">
                    Successfully uploaded
                  </Typography>
                  <Typography variant="body2" color="primary" fontWeight={700}>
                    We will get back to you shortly
                  </Typography>
                </Stack>
              ) : (
                <Stack>
                  <Stack>
                    <Typography variant="h1" color="primary">
                      Almost done!
                    </Typography>
                    <Typography variant="h1" color="primary">
                      Upload your CV now.
                    </Typography>
                    <Typography pt={1} color="textSecondary" variant="body2">
                      Your data will be used exclusively for the application process (secure &
                      GDPR-compliant).
                    </Typography>
                  </Stack>
                  <Stack
                    direction={cvFile ? 'column' : 'row'}
                    width="100%"
                    height={183}
                    mt={5}
                    border="2.5px dashed #220D77"
                    borderRadius={7}
                    alignItems="center"
                    justifyContent="center"
                    spacing={cvFile ? 1 : 5}
                    onDragOver={(e) => {
                      e.preventDefault()
                      setIsDragging(true)
                    }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={(e: React.DragEvent<HTMLDivElement>) => {
                      e.preventDefault()
                      setIsDragging(false)
                      const file = e.dataTransfer.files[0]
                      if (!file || file.type !== 'application/pdf') {
                        setSubmitError('Please select a PDF file.')
                        return
                      }
                      const sizeError = validateFileSize([file])
                      if (sizeError) {
                        setSubmitError(sizeError)
                        return
                      }
                      setCvFile(file)
                      setSubmitError('')
                    }}
                    sx={{
                      cursor: 'pointer',
                      opacity: isDragging ? 0.5 : 1,
                      transition: 'opacity 0.2s ease',
                      backgroundColor: isDragging ? '#f0f0f0' : 'transparent',
                    }}
                  >
                    <form onSubmit={handleSubmit}>
                      {cvFile ? (
                        <Stack spacing={1} alignItems="center">
                          <Typography variant="h3" color="primary">
                            {cvFile.name}
                          </Typography>
                          <Stack direction="row" spacing={1}>
                            <Button
                              variant="outlined"
                              onClick={() => {
                                setCvFile(null)
                                setSubmitError('')
                              }}
                            >
                              Delete
                            </Button>
                            <Button
                              type="submit"
                              variant="contained"
                              disabled={!cvFile || isSubmitting}
                            >
                              {isSubmitting ? 'Uploading...' : 'Submit CV'}
                            </Button>
                          </Stack>
                        </Stack>
                      ) : (
                        <Stack direction="row" justifyContent="center" spacing={2}>
                          <FileUpload
                            sx={{
                              fontSize: 40,
                            }}
                            htmlColor="primary"
                          />
                          <Stack>
                            <Typography color="primary" variant="h3">
                              Drag and drop your CV here
                            </Typography>
                            <Typography color="primary" variant="body2">
                              doc, docx, pdf, rtf, txt (Min 8KB, Max 6MB per file)
                            </Typography>
                            {submitError && (
                              <Typography color="error" variant="body2">
                                {submitError}
                              </Typography>
                            )}
                          </Stack>
                          <input
                            accept=".pdf"
                            style={{ display: 'none' }}
                            id="cv-upload"
                            type="file"
                            onChange={handleFileChange}
                          />

                          <label htmlFor="cv-upload">
                            <Button variant="contained" component="span">
                              Select file
                            </Button>
                          </label>
                        </Stack>
                      )}
                    </form>
                  </Stack>
                </Stack>
              )}
              <Typography pt={3} variant="body2" color="textSecondary">
                Employers use your CV to assess whether you have the right experience for the
                position.
              </Typography>
            </Stack>
            <Typography pt={5} variant="h3" color="primary">
              What is recruspin?
            </Typography>
            <Typography pt={3}>
              <Link
                target="_blank"
                href="https://recruspin.com/"
                fontWeight={700}
                sx={{ cursor: 'pointer' }}
              >
                recruspin
              </Link>{' '}
              supports us as a technical service provider for secure data uploads. Your application
              naturally remains directly with Foodmasters.
            </Typography>
          </Grid>

          {/* Right side - Application Information and CV Upload */}
          <Grid item xs={12} lg={5}>
            <Grid container spacing={3}>
              {/* Lead Information */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="body2" color="primary" fontWeight={700}>
                      Your application details
                    </Typography>
                    <Stack mt={3} spacing={2}>
                      <Stack direction="row" spacing={7} alignItems="center">
                        <Stack direction="row" spacing={1} alignItems="center">
                          <IconUser width={20} height={20} color="#220d77" />
                          <Typography variant="body2" color="textSecondary">
                            Name
                          </Typography>
                        </Stack>
                        <Typography variant="body1" fontWeight={700} color="primary">
                          {formattedFields[0].values[0]}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={7} alignItems="center">
                        <Stack direction="row" spacing={1} alignItems="center">
                          <MailOutline width={20} height={20} htmlColor="primary" />
                          <Typography variant="body2" color="textSecondary">
                            E-mail
                          </Typography>
                        </Stack>
                        <Typography variant="body1" fontWeight={700} color="primary">
                          {formattedFields[1].values[0]}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={7} alignItems="center">
                        <Stack direction="row" spacing={1} alignItems="center">
                          <PhoneOutlined width={20} height={20} htmlColor="primary" />
                          <Typography variant="body2" color="textSecondary">
                            Phone
                          </Typography>
                        </Stack>
                        <Typography variant="body1" fontWeight={700} color="primary">
                          {formattedFields[2].values[0]}
                        </Typography>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </div>
  )
}

export default ApplicationPage
